import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useLingui } from "@lingui/react/macro";
import { msg } from "@lingui/core/macro";
import { useOnboarding } from "@/features/onboarding/OnboardingContext";

export const Route = createFileRoute("/(public)/onboarding/organization")({
  component: OrganizationSetupPage,
});

// `value` is stored as-is; only the displayed `label` is translated.
const COUNTRIES = [
  { value: "United States", label: msg`United States` },
  { value: "Canada", label: msg`Canada` },
  { value: "United Kingdom", label: msg`United Kingdom` },
  { value: "Australia", label: msg`Australia` },
  { value: "Germany", label: msg`Germany` },
  { value: "France", label: msg`France` },
  { value: "Brazil", label: msg`Brazil` },
  { value: "Mexico", label: msg`Mexico` },
  { value: "India", label: msg`India` },
  { value: "Japan", label: msg`Japan` },
];

const TIMEZONES = [
  "America/New_York (UTC-5)", "America/Chicago (UTC-6)",
  "America/Denver (UTC-7)", "America/Los_Angeles (UTC-8)",
  "America/Anchorage (UTC-9)", "Pacific/Honolulu (UTC-10)",
  "Europe/London (UTC+0)", "Europe/Paris (UTC+1)",
  "Europe/Berlin (UTC+1)", "Asia/Tokyo (UTC+9)",
  "Australia/Sydney (UTC+11)",
];

function OrganizationSetupPage() {
  const { t, i18n } = useLingui();
  const navigate = useNavigate();
  const { orgName: savedName, orgCountry: savedCountry, orgTimezone: savedTimezone, setOrgInfo } = useOnboarding();

  const [orgName, setOrgName] = useState(savedName);
  const [country, setCountry] = useState(savedCountry);
  const [timezone, setTimezone] = useState(savedTimezone);

  const handleContinue = () => {
    if (!orgName.trim()) {
      toast.error(t`Organization name is required`);
      return;
    }
    setOrgInfo(orgName.trim(), country, timezone);
    navigate({ to: "/onboarding/company" });
  };

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-2">
        <h1 className="font-semibold leading-tight text-(--brand-black)" style={{ fontSize: "2rem" }}>
          {t`Set up your organization`}
        </h1>
        <p className="text-sm leading-relaxed text-(--gray-500)">
          {t`Your organization is the workspace where you manage kitchens, recipes, locations, and team members.`}
        </p>
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-(--brand-black)">
            {t`Organization name`}
          </label>
          <input
            type="text"
            value={orgName}
            onChange={(e) => setOrgName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleContinue(); }}
            placeholder={t`Enter your organization name...`}
            className="w-full h-10 rounded-md px-3 text-sm outline-none transition-shadow border border-(--gray-200) text-(--brand-black) bg-(--brand-white)"
            onFocus={(e) => { e.currentTarget.style.boxShadow = "0 0 0 3px rgba(109,112,98,0.15)"; e.currentTarget.style.borderColor = "var(--gray-600)"; }}
            onBlur={(e) => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.borderColor = "var(--gray-200)"; }}
          />
        </div>

        <div className="flex gap-3">
          {(["Country", "Timezone"] as const).map((field) => {
            const value = field === "Country" ? country : timezone;
            const setter = field === "Country" ? setCountry : setTimezone;
            const options = field === "Country"
              ? COUNTRIES.map((c) => ({ value: c.value, label: i18n._(c.label) }))
              : TIMEZONES.map((tz) => ({ value: tz, label: tz }));
            const fieldLabel = field === "Country" ? t`Country` : t`Timezone`;
            const selectPlaceholder = field === "Country" ? t`Select country...` : t`Select timezone...`;
            return (
              <div key={field} className="flex flex-col gap-1.5 flex-1">
                <label className="text-sm font-medium text-(--brand-black)">{fieldLabel}</label>
                <select
                  value={value}
                  onChange={(e) => setter(e.target.value)}
                  className="w-full h-10 rounded-md px-3 text-sm outline-none transition-shadow appearance-none border border-(--gray-200) bg-(--brand-white)"
                  style={{
                    color: value ? "var(--brand-black)" : "var(--gray-400)",
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23afafaf' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,
                    backgroundRepeat: "no-repeat", backgroundPosition: "right 0.75rem center", paddingRight: "2rem" }}
                  onFocus={(e) => { e.currentTarget.style.boxShadow = "0 0 0 3px rgba(109,112,98,0.15)"; e.currentTarget.style.borderColor = "var(--gray-600)"; }}
                  onBlur={(e) => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.borderColor = "var(--gray-200)"; }}
                >
                  <option value="" disabled>{selectPlaceholder}</option>
                  {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            );
          })}
        </div>
      </div>

      <button
        type="button"
        onClick={handleContinue}
        className="w-full h-11 flex items-center justify-center gap-2 text-sm font-semibold text-white rounded-md transition-opacity hover:opacity-90 bg-(--brand-black)"
      >
        {t`Continue`} <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}
